import React, { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider, toast } from './components/UI';
import { supabase } from './services/supabase';
import BottomNav from './components/BottomNav';
import GlobalMenu from './components/GlobalMenu';
import LoginScreen from './screens/LoginScreen';
import HomeScreen from './screens/HomeScreen';
import CubaScreen from './screens/CubaScreen';
import ClicksScreen from './screens/ClicksScreen';
import MoreScreen from './screens/MoreScreen';
import AsistentesScreen from './screens/AsistentesScreen';
import VuelosGuyanaPublicScreen from './screens/VuelosGuyanaPublicScreen';
import NuestrosClientesPublicScreen from './screens/NuestrosClientesPublicScreen';
import NuestrosClientesScreen from './screens/NuestrosClientesScreen';
import { setCanalActivo, limpiarCanalActivo, setGestorActivo, limpiarGestorActivo } from './services/vuelosGuyanaService';
import { storePreviewToken, storeResenaInvite } from './services/nuestrosClientesService';
import { GRUPO_WHATSAPP_URL } from './config/links';

function InstallBanner() {
  const [prompt, setPrompt] = useState(null);
  const [show, setShow] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches) return;

    const handler = (e) => {
      e.preventDefault();
      setPrompt(e);
      setShow(true);
    };

    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', () => {
      setShow(false);
      setInstalled(true);
    });

    const timer = setTimeout(() => {
      if (!installed) setShow(true);
    }, 3000);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      clearTimeout(timer);
    };
  }, []);

  if (!show) return null;

  const handleInstall = async () => {
    if (prompt) {
      await prompt.prompt();
      const { outcome } = await prompt.userChoice;
      if (outcome === 'accepted') setShow(false);
      return;
    }
    // Sin evento capturado (típico cuando la app ya está instalada en el
    // celular pero se abrió este link en una pestaña normal de Chrome, no
    // dentro de la app instalada) -- Chrome no vuelve a ofrecer el diálogo
    // nativo de instalar. Antes el botón no hacía nada visible, lo cual
    // parecía roto; ahora explica el paso manual que sí funciona en ese caso.
    toast('Usa el menú (⋮) de Chrome arriba a la derecha → "Agregar a pantalla de inicio".');
  };

  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);

  return (
    <div style={{
      position: 'fixed', bottom: 80, left: 12, right: 12,
      background: 'linear-gradient(135deg, #0F2235, #0A1628)',
      border: '1px solid rgba(255,107,0,0.4)',
      borderRadius: 18, padding: '16px 18px',
      zIndex: 999, boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', gap: 14,
    }}>
      <div style={{ fontSize: 36, flexShrink: 0 }}>📲</div>
      <div style={{ flex: 1 }}>
        <p style={{ fontSize: 14, fontWeight: 800, color: '#FFFFFF', marginBottom: 2 }}>
          Instala la app
        </p>
        <p style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.4 }}>
          {isIOS
            ? 'Toca el botón compartir ↑ y luego "Agregar a inicio". En iPhone, Apple solo permite recibir notificaciones de CLICKs y promociones si instalas la app así.'
            : 'Accede más rápido desde tu pantalla de inicio y recibe notificaciones de CLICKs y promociones.'}
        </p>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
        {!isIOS && (
          <button onClick={handleInstall} style={{
            background: 'var(--orange)', color: 'white',
            fontWeight: 700, fontSize: 12, borderRadius: 9,
            padding: '8px 14px', whiteSpace: 'nowrap',
          }}>
            Instalar
          </button>
        )}
        <button onClick={() => setShow(false)} style={{
          background: 'transparent', color: 'var(--muted)',
          fontSize: 11, borderRadius: 9, padding: '4px 8px',
          border: '1px solid var(--navy-border)',
        }}>
          Ahora no
        </button>
      </div>
    </div>
  );
}

const VALID_TABS = ['home', 'cuba', 'clicks', 'asesores', 'more', 'nuestros_clientes'];

function AppContent() {
  const { isLoggedIn, loading } = useAuth();
  // Las notificaciones push abren la app con ?tab=clicks (o similar) para
  // llevar directo a la pantalla correspondiente -- no hay router real,
  // así que se lee una sola vez al montar. El link de invitación a dejar
  // reseña usa el mismo mecanismo: ?tab=nuestros_clientes&resena=<codigo>.
  const [tab, setTab] = useState(() => {
    const t = new URLSearchParams(window.location.search).get('tab');
    return VALID_TABS.includes(t) ? t : 'home';
  });
  const [cubaService, setCubaService] = useState(null);
  const [openAdmin, setOpenAdmin] = useState(false);
  // Deep-link de las notificaciones push del admin: además de ?tab=more,
  // ?admin=<tab del panel> (y opcionalmente &sub=<sub-tab>) llevan directo a
  // la sección exacta que generó la notificación (canjes, solicitudes,
  // reseñas...) en vez de dejarlo en la pantalla "Más" a secas.
  const parseAdminParam = (params) => {
    const adminTab = params.get('admin');
    return adminTab ? { tab: adminTab, sub: params.get('sub'), key: Date.now() } : null;
  };
  const [adminDeepLink, setAdminDeepLink] = useState(() => parseAdminParam(new URLSearchParams(window.location.search)));

  useEffect(() => {
    supabase.auth.getSession();
    const resenaParam = new URLSearchParams(window.location.search).get('resena');
    if (resenaParam) storeResenaInvite(resenaParam);
  }, []);

  // Cuando la app ya está abierta (en background) y se toca una
  // notificación, el service worker no navega la página -- le avisa a la
  // app YA corriendo por postMessage para que ella misma cambie de
  // pantalla, sin recargar nada (ver notificationclick en sw.js).
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const onMessage = (event) => {
      if (event.data?.type !== 'nc-navigate' || !event.data.url) return;
      const target = new URL(event.data.url, window.location.origin);
      const t = target.searchParams.get('tab');
      if (VALID_TABS.includes(t)) setTab(t);
      setAdminDeepLink(parseAdminParam(target.searchParams));
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, []);

  const handleNavigate = (target, service) => {
    setTab(target);
    if (service) setCubaService(service);
  };

  const handleOpenAdmin = () => {
    setTab('more');
    setOpenAdmin(true);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: 'var(--bg)' }}>
        <span className="spinner" />
      </div>
    );
  }

  if (!isLoggedIn) return <LoginScreen />;

  return (
    <>
      {tab === 'home'      && <HomeScreen onNavigate={handleNavigate} />}
      {tab === 'cuba'      && <CubaScreen initialService={cubaService} />}
      {tab === 'clicks'    && <ClicksScreen />}
      {tab === 'asesores'  && <AsistentesScreen />}
      {tab === 'more'      && <MoreScreen openAdmin={openAdmin} onAdminOpened={() => setOpenAdmin(false)} adminDeepLink={adminDeepLink} />}
      {tab === 'nuestros_clientes' && <NuestrosClientesScreen />}
      <GlobalMenu onNavigate={handleNavigate} onOpenAdmin={handleOpenAdmin} />
      <BottomNav active={tab} onChange={setTab} />
    </>
  );
}

export default function App() {
  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get('ref');
    if (ref) localStorage.setItem('pending_referral_code', ref.toUpperCase());
  }, []);

  // /grupo-whatsapp lo redirige Netlify (netlify.toml). Este respaldo es para celulares que todavía
  // tienen el service worker anterior, que atiende la navegación como si fuera una pantalla de la app.
  if (window.location.pathname.replace(/\/$/, '') === '/grupo-whatsapp') {
    window.location.replace(GRUPO_WHATSAPP_URL);
    return null;
  }

  // Ruta pública sin login: /vuelos-guyana. Se resuelve antes que cualquier
  // gate de autenticación -- netlify.toml ya redirige todo a index.html,
  // así que basta con leer el pathname en el cliente.
  //
  // El link de Luis Omar es /vuelos-guyana?j=1 (query param, NO un segmento
  // de ruta extra tipo /vuelos-guyana/j) -- el build usa "homepage": "."
  // en package.json, que genera rutas relativas para los assets. Un nivel
  // extra en el path (/vuelos-guyana/j) rompe esas rutas relativas y la app
  // nunca llega a montar. El query param no tiene ese problema porque el
  // pathname sigue siendo exactamente /vuelos-guyana.
  if (window.location.pathname.replace(/\/$/, '') === '/vuelos-guyana') {
    const jParam = new URLSearchParams(window.location.search).get('j');
    // Marcar un canal/gestor SIEMPRE limpia el otro -- un dispositivo solo
    // puede seguir un link a la vez, y tiene que ganar el que se abrió
    // ahora, no uno viejo que haya quedado de una prueba anterior (bug real:
    // probar el link de Luis Omar y semanas después el de un gestor en el
    // mismo teléfono dejaba los dos marcados, y el canal viejo ganaba
    // siempre por prioridad en getRemitenteActivo -- el mensaje de WhatsApp
    // salía "vengo de parte de Luis Omar" aunque se hubiera abierto el link
    // de otro gestor).
    if (jParam === '1') { setCanalActivo('j'); limpiarGestorActivo(); }
    // ?j=0 limpia el canal de este dispositivo -- para Yoedis (o cualquiera
    // probando) después de haber abierto el link de Luis Omar por error o
    // para pruebas, sin tener que esperar los 45 días o borrar localStorage
    // a mano.
    else if (jParam === '0') limpiarCanalActivo();

    // Link de un gestor: /vuelos-guyana?g=<codigo>. El código lo define el
    // admin al crear el gestor (Tema 4) -- se resuelve contra la base de
    // datos ni bien carga VuelosGuyanaFlow (sincronizarGestorActivo), acá
    // solo se guarda la marca. ?g=0 limpia el gestor marcado, mismo criterio
    // que ?j=0.
    const gParam = new URLSearchParams(window.location.search).get('g');
    if (gParam && gParam !== '0') { setGestorActivo(gParam); limpiarCanalActivo(); }
    else if (gParam === '0') limpiarGestorActivo();

    // ?ncpreview=<token> -- vista previa de "Nuestros Clientes" desde este
    // mismo link público, sin tocar el canal/gestor de arriba.
    const previewParam = new URLSearchParams(window.location.search).get('ncpreview');
    if (previewParam) storePreviewToken(previewParam);

    return <VuelosGuyanaPublicScreen />;
  }

  // Ruta pública sin login: /nuestros-clientes. ?preview=<token> guarda el
  // token de vista previa en este dispositivo (solo Yoedis lo conoce
  // mientras clientes_config.publicado siga en false) -- ver
  // nuestrosClientesService.js. ?album=<share_token> resalta ese álbum.
  if (window.location.pathname.replace(/\/$/, '') === '/nuestros-clientes') {
    const params = new URLSearchParams(window.location.search);
    const preview = params.get('preview');
    if (preview) storePreviewToken(preview);
    return <NuestrosClientesPublicScreen highlightShareToken={params.get('album')} />;
  }

  return (
    <AuthProvider>
      <ToastProvider />
      <InstallBanner />
      <AppContent />
    </AuthProvider>
  );
}
